import React, { useEffect, useState } from "react";
import useContract from "../hooks/useContract";
import {
  useAppKitAccount,
  useAppKit,
} from "@reown/appkit/react";
import { Contract, formatUnits, JsonRpcProvider } from "ethers";

const RPC_URL = "https://bsc-dataseed.binance.org";

const Income = () => {
  const { address } = useAppKitAccount();
  const { open } = useAppKit();

  const { getFullDashboard, claimAll } = useContract();

  const [data, setData] = useState({
    totalPackages: 0,
    activePackages: 0,
    totalSpent: 0,
    totalClaimed: 0,
    claimable: 0,
    referralTotal: 0,
  });

  const [loading, setLoading] = useState(false);
  const [kindPrice, setKindPrice] = useState(0);

  const provider = new JsonRpcProvider(RPC_URL);

  const KIND_ADDRESS = "0x41f52A42091A6B2146561bF05b722Ad1d0e46f8b";
  const KIND_BNB_PAIR = "0x628C0fe6DA9854EDf0f3E2C7657af6ecf29C740E";

  // 🔥 FETCH LIVE PRICE
  const fetchKindPrice = async () => {
    try {
      const pair = new Contract(
        KIND_BNB_PAIR,
        [
          "function getReserves() view returns (uint112,uint112,uint32)",
          "function token0() view returns (address)",
        ],
        provider
      );

      const reserves = await pair.getReserves();
      const token0 = await pair.token0();

      let kindReserve, bnbReserve;

      if (token0.toLowerCase() === KIND_ADDRESS.toLowerCase()) {
        kindReserve = reserves[0];
        bnbReserve = reserves[1];
      } else {
        kindReserve = reserves[1];
        bnbReserve = reserves[0];
      }

      const kindReserveNum = parseFloat(formatUnits(kindReserve, 18));
      const bnbReserveNum = parseFloat(formatUnits(bnbReserve, 18));

      const kindPerBNB = bnbReserveNum / kindReserveNum;

      const priceFeed = new Contract(
        "0x0567F2323251f0Aab15c8dFb1967E4e8A7D42aeE",
        [
          "function latestRoundData() view returns (uint80,int256,uint256,uint256,uint80)",
          "function decimals() view returns (uint8)",
        ],
        provider
      );

      const roundData = await priceFeed.latestRoundData();
      const decimals = await priceFeed.decimals();

      const bnbUsd = parseFloat(formatUnits(roundData[1], decimals));

      setKindPrice(kindPerBNB * bnbUsd);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchKindPrice();
    const i = setInterval(fetchKindPrice, 15000);
    return () => clearInterval(i);
  }, []);

  const getUSD = (amount) => {
    if (!kindPrice || isNaN(kindPrice)) return "0.00";
    return (Number(amount) * Number(kindPrice)).toFixed(2);
  };

  // 🔥 LOAD USER DATA
  useEffect(() => {
    if (!address) return;

    const load = async () => {
      try {
        const res = await getFullDashboard();
        setData(res);
      } catch (err) {
        console.log(err);
      }
    };

    load();
  }, [address, getFullDashboard]);

  // 💰 CLAIM
  const handleClaim = async () => {
    try {
      setLoading(true);

      await claimAll();

      const updated = await getFullDashboard();
      setData(updated);

      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  // ✅ CONNECT WALLET UI
  if (!address) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black px-4">
        <div className="w-full max-w-md bg-gradient-to-br from-[#0f1b2d] to-[#1a0f1f] border border-blue-500 rounded-2xl p-8 text-center">

          <div className="w-16 h-16 mx-auto mb-4 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>

          <h2 className="text-2xl font-bold text-blue-400 mb-2">
            Connect Wallet
          </h2>

          <p className="text-gray-400 mb-6 text-sm">
            Please connect your wallet to view income
          </p>

          <button
            onClick={() => open()}
            className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 py-3 rounded-xl font-semibold"
          >
            Connect Wallet
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="text-white min-h-screen">

      {/* HEADER */}
      <div className="bg-gradient-to-r from-[#0f1b2d] via-[#0a0f1c] to-[#2a0f2f] p-6 md:p-8 rounded-b-2xl">

        <div className="flex flex-col md:flex-row justify-between gap-4">

          <div>
            <h1 className="text-2xl md:text-4xl font-bold text-blue-400">
              Income
            </h1>

            <div className="mt-3 flex flex-wrap gap-3 text-sm text-gray-300">

              <span className="text-yellow-400">
                KIND: ${kindPrice > 0 ? kindPrice.toFixed(6) : "Loading..."}
              </span>

              <span>•</span>

              <span className="text-green-400">
                {Number(data.activePackages).toFixed(2)} Active Packages
              </span>

            </div>

          </div>

        </div>

      </div>

      {/* STATS */}
      <div className="p-4 md:p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">

        <Card title="CLAIMABLE REWARDS" value={data.claimable} color="green" getUSD={getUSD} />
        <Card title="TOTAL CLAIMED" value={data.totalClaimed} color="blue" getUSD={getUSD} />
        <Card title="REFERRAL EARNINGS" value={data.referralTotal} color="purple" getUSD={getUSD} />
        <Card title="TOTAL INVESTED" value={data.totalSpent} color="yellow" getUSD={getUSD} />
        <Card title="TOTAL PACKAGES" value={data.totalPackages} color="cyan" />
        <Card title="ACTIVE PACKAGES" value={data.activePackages} color="orange" />

      </div>

    </div>
  );
};

// CARD
const Card = ({ title, value, color, getUSD }) => {
  const colors = {
    green: "from-green-500/20 to-green-900/20 border-green-500 text-green-400",
    blue: "from-blue-500/20 to-blue-900/20 border-blue-500 text-blue-400",
    purple: "from-purple-500/20 to-purple-900/20 border-purple-500 text-purple-400",
    yellow: "from-yellow-500/20 to-yellow-900/20 border-yellow-500 text-yellow-400",
    cyan: "from-cyan-500/20 to-cyan-900/20 border-cyan-500 text-cyan-400",
    orange: "from-orange-500/20 to-orange-900/20 border-orange-500 text-orange-400",
  };

  const isPackage = title.includes("PACKAGES");

  return (
    <div className={`p-5 md:p-6 rounded-2xl bg-gradient-to-br ${colors[color]} border transition hover:scale-[1.02]`}>
      <p className="text-gray-400 text-xs md:text-sm mb-2">{title}</p>

      <h2 className="text-lg md:text-2xl font-bold text-white">
        {Number(value).toFixed(2)}

        {!isPackage && (
          <>
            {" "}KIND
            <div className="text-xs md:text-sm text-gray-400 mt-1">
              (${getUSD ? getUSD(value) : "0.00"})
            </div>
          </>
        )}
      </h2>
    </div>
  );
};

export default Income;